import React, { useCallback, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
	Alert,
	Box,
	Button,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const API = process.env.REACT_APP_LAMBDA_ARTEH_API_DOMAIN;

interface Member {
	companyID: number;
	name: string;
}
interface YearRow {
	year: number;
	reportingEntities: number;
	scope1Tonnes: number;
	scope2Tonnes: number;
	scope3Tonnes: number;
	totalTonnes: number;
}

/** Multi-entity consolidation (P4): group companies, consolidated baselines. */
const Groups: React.FC = () => {
	const { getAccessTokenSilently } = useAuth0();
	const [group, setGroup] = useState<{
		groupID: number;
		groupName: string;
	} | null>(null);
	const [members, setMembers] = useState<Member[]>([]);
	const [years, setYears] = useState<YearRow[]>([]);
	const [msg, setMsg] = useState<string | null>(null);
	const [groupName, setGroupName] = useState("");
	const [memberId, setMemberId] = useState("");

	const call = useCallback(
		async (path: string, init?: RequestInit) => {
			const token = await getAccessTokenSilently();
			const res = await fetch(`${API}${path}`, {
				...init,
				headers: {
					Authorization: `Bearer ${token}`,
					"Content-Type": "application/json",
				},
			});
			const data = await res.json().catch(() => ({}));
			return { ok: res.ok, data: data.Data || data };
		},
		[getAccessTokenSilently],
	);

	const load = useCallback(async () => {
		const r = await call("/api/groups/overview");
		if (r.ok) {
			setGroup(r.data.group);
			setMembers(r.data.members || []);
			setYears(r.data.years || []);
		}
	}, [call]);

	useEffect(() => {
		load();
	}, [load]);

	const act = async (path: string, init: RequestInit) => {
		const r = await call(path, init);
		setMsg(r.data.Message || r.data.message || null);
		if (r.ok) await load();
	};

	return (
		<Box maxWidth={1000} margin="0 auto" padding={3}>
			<Typography variant="h5" gutterBottom>
				Group consolidation
			</Typography>
			<Typography variant="body2" color="text.secondary" gutterBottom>
				Consolidate emissions across a group of entities. Figures sum
				each member&apos;s <strong>locked baselines</strong> — only
				assured numbers roll up, which is what group reporting requires.
			</Typography>

			{msg && (
				<Alert
					severity="info"
					sx={{ my: 2 }}
					onClose={() => setMsg(null)}
				>
					{msg}
				</Alert>
			)}

			{!group ? (
				<Paper
					elevation={0}
					sx={{ p: 2, my: 2, border: "1px solid rgba(0,23,28,0.10)" }}
				>
					<Box
						display="flex"
						gap={2}
						alignItems="center"
						flexWrap="wrap"
					>
						<TextField
							label="Group name (e.g. Acme Group)"
							size="small"
							value={groupName}
							onChange={(e) => setGroupName(e.target.value)}
							sx={{ minWidth: 260 }}
						/>
						<Button
							variant="contained"
							color="secondary"
							disabled={groupName.trim().length < 2}
							onClick={() =>
								act("/api/groups", {
									method: "POST",
									body: JSON.stringify({ groupName }),
								})
							}
						>
							Create group
						</Button>
					</Box>
				</Paper>
			) : (
				<>
					<Paper
						elevation={0}
						sx={{
							p: 2,
							my: 2,
							border: "1px solid rgba(0,23,28,0.10)",
						}}
					>
						<Typography variant="subtitle1" gutterBottom>
							{group.groupName} — member entities
						</Typography>
						<Table size="small">
							<TableBody>
								{members.map((m) => (
									<TableRow key={m.companyID}>
										<TableCell>{m.name}</TableCell>
										<TableCell width={60}>
											#{m.companyID}
										</TableCell>
										<TableCell align="right">
											<IconButton
												size="small"
												aria-label="Remove from group"
												onClick={() =>
													act(
														`/api/groups/members?memberCompanyId=${m.companyID}`,
														{ method: "DELETE" },
													)
												}
											>
												<DeleteOutlineIcon fontSize="small" />
											</IconButton>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
						<Box display="flex" gap={2} alignItems="center" mt={2}>
							<TextField
								label="Add company by ID"
								size="small"
								value={memberId}
								onChange={(e) => setMemberId(e.target.value)}
								sx={{ width: 180 }}
								helperText="You must be a member of that company"
							/>
							<Button
								variant="outlined"
								color="secondary"
								disabled={!memberId.trim()}
								onClick={() =>
									act("/api/groups/members", {
										method: "POST",
										body: JSON.stringify({
											memberCompanyId: memberId,
										}),
									})
								}
							>
								Add
							</Button>
						</Box>
					</Paper>

					<Paper
						elevation={0}
						sx={{
							p: 2,
							my: 2,
							border: "1px solid rgba(0,23,28,0.10)",
						}}
					>
						<Typography variant="subtitle1" gutterBottom>
							Consolidated emissions (locked baselines)
						</Typography>
						{years.length === 0 ? (
							<Typography variant="body2" color="text.secondary">
								No locked baselines across the group yet. Each
								entity locks its year on the Baselines page;
								totals appear here.
							</Typography>
						) : (
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>Year</TableCell>
										<TableCell>Entities</TableCell>
										<TableCell>Scope 1</TableCell>
										<TableCell>Scope 2</TableCell>
										<TableCell>Scope 3</TableCell>
										<TableCell>Total (t CO2e)</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{years.map((y) => (
										<TableRow key={y.year}>
											<TableCell>{y.year}</TableCell>
											<TableCell>
												{y.reportingEntities}
											</TableCell>
											<TableCell>
												{y.scope1Tonnes}
											</TableCell>
											<TableCell>
												{y.scope2Tonnes}
											</TableCell>
											<TableCell>
												{y.scope3Tonnes}
											</TableCell>
											<TableCell>
												{y.totalTonnes}
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						)}
					</Paper>
				</>
			)}
		</Box>
	);
};

export default Groups;
